import React, { useState } from "react";
import { Page } from "components/Page";
import { Tabs, Tab, TabsList, TabsContent } from "components/Tabs";
import { startOfToday } from "date-fns";
import { Banners } from "components/Banners";
import { PermissionsRequired } from "library";
import { NextToJump } from "@/features/racing/components/Tabs/NextToJump";
import { RacingTitleFilter } from "@/features/racing/components/RacingTitleFilter";
import { useAnnouncementCards } from "@/hooks/useAnnouncementCards";
import { AnnouncementCards } from "@/components/AnnouncementCards";
import { Today } from "@/features/racing/components/Tabs/Today";
import { Yesterday } from "@/features/racing/components/Tabs/Yesterday";
import { Tomorrow } from "@/features/racing/components/Tabs/Tomorrow";
import { Saturday } from "@/features/racing/components/Tabs/Saturday";
import { Sunday } from "@/features/racing/components/Tabs/Sunday";
import { CustomDate } from "features/racing/components/Tabs/CustomDate";
import { DateSelector } from "@/features/racing/components/DateSelector";
import { Redirect } from "@reach/router";
import { RacingSkeleton } from "@/features/racing/components/RacingSkeleton";

import * as styles from "./Racing.module.scss";
import { useLocation } from "@/hooks/useLocation";

export enum Days {
  mon = 1,
  tue = 2,
  wed = 3,
  thu = 4,
  fri = 5,
  sat = 6,
  sun = 7,
}

type RacingProps = {
  title?: string;
};

export const Racing = ({ title }: RacingProps) => {
  const cards = useAnnouncementCards("racing");

  const { search } = useLocation();
  const showSaturday = [Days.wed, Days.thu].includes(startOfToday().getDay());
  const showSunday = [Days.thu, Days.fri].includes(startOfToday().getDay());
  const [selectedTab, setSelectedTab] = useState<string>("/next-to-jump");
  const titlePrefix = title ? `${title}/` : "";

  return (
    <Page
      variant={`full`}
      title={`Racing`}
      showSeparator={false}
      banner={<Banners />}
      hideTitleOnMobile
    >
      <PermissionsRequired
        permissionsRequired={["viewBettingMarkets", "viewRacingMarkets"]}
        skeleton={<RacingSkeleton tab={selectedTab} />}
      >
        <Tabs
          className={styles.tabsContainer}
          basePath={`/racing/${titlePrefix}betting`}
          isValueDerivedFromRouter
          defaultValue={`next-to-jump`}
          onValueChange={(value) => setSelectedTab(value)}
        >
          <TabsList
            className={styles.tabs}
            widget={<DateSelector selectedTab={selectedTab} />}
          >
            <Tab to={`/yesterday${search}`}>Yesterday</Tab>
            <Tab to={`/next-to-jump${search}`}>Next to Jump</Tab>
            <Tab to={`/today${search}`}>Today</Tab>
            <Tab to={`/tomorrow${search}`}>Tomorrow</Tab>
            {showSaturday && <Tab to={`/saturday${search}`}>Saturday</Tab>}
            {showSunday && <Tab to={`/sunday${search}`}>Sunday</Tab>}
          </TabsList>
          <RacingTitleFilter selectedTab={selectedTab} />
          <AnnouncementCards cards={cards} />
          <TabsContent>
            <Redirect from={`/`} to={`next-to-jump`} noThrow />
            <Yesterday path={`/yesterday`} />
            <NextToJump path={`/next-to-jump`} />
            <Today path={`/today`} />
            <Tomorrow path={`/tomorrow`} />
            <Saturday path={`/saturday`} />
            <Sunday path={`/sunday`} />
            <CustomDate path={`/:date`} />
          </TabsContent>
        </Tabs>
      </PermissionsRequired>
    </Page>
  );
};
